import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useForm, Controller } from 'react-hook-form';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { 
  X, 
  MapPin, 
  Calendar, 
  Clock, 
  Car, 
  Calculator,
  MessageCircle,
  Phone,
  IndianRupee
} from 'lucide-react';

const BookingModal = ({ isOpen, onClose }) => {
  const [bookingStep, setBookingStep] = useState(1);
  const [fareEstimate, setFareEstimate] = useState(null);
  const [selectedVehicle, setSelectedVehicle] = useState('tata');
  
  const { control, handleSubmit, watch, formState: { errors } } = useForm({
    defaultValues: {
      pickupLocation: '',
      dropLocation: '',
      serviceType: 'airport',
      bookingDate: new Date(),
      bookingTime: new Date(),
      vehicle: 'tata',
      passengers: 1
    }
  });

  const watchedValues = watch();

  const vehicles = {
    tata: { name: 'Tata T Express', capacity: 7, baseRate: 15, image: 'https://images.unsplash.com/photo-1625998259975-b243bf480751' },
    byd: { name: 'BYD eMax 7', capacity: 7, baseRate: 18, image: 'https://images.unsplash.com/photo-1569780713905-3aab7e29a7e5' }
  };

  const locations = {
    delhi: ['Connaught Place', 'India Gate', 'Red Fort', 'Karol Bagh', 'Chandni Chowk', 'Lajpat Nagar'],
    gurgaon: ['Cyber Hub', 'Sector 14', 'Golf Course Road', 'MG Road', 'Sector 29', 'DLF Phase 1'],
    noida: ['Sector 18', 'Sector 62', 'Film City', 'City Centre', 'Sector 15', 'Sector 37'],
    airport: ['IGI Airport Terminal 1', 'IGI Airport Terminal 2', 'IGI Airport Terminal 3']
  };

  const calculateFare = () => {
    const vehicle = vehicles[watchedValues.vehicle];
    const serviceType = watchedValues.serviceType;
    let baseFare = 0;
    let distance = 0;

    // Estimate distance and fare based on service type
    switch (serviceType) {
      case 'airport':
        distance = 35; // Average distance to airport
        baseFare = distance * vehicle.baseRate;
        break;
      case 'hourly':
        baseFare = vehicle.baseRate * 50; // Per hour rate
        break;
      case 'daily':
        baseFare = vehicle.baseRate * 400; // Per day rate
        break;
      case 'intercity':
        distance = 200; // Average intercity distance
        baseFare = distance * vehicle.baseRate * 0.8; // Discounted rate
        break;
      default:
        distance = 25;
        baseFare = distance * vehicle.baseRate;
    }

    // Add surge pricing for peak hours
    const hour = watchedValues.bookingTime.getHours();
    if ((hour >= 7 && hour <= 10) || (hour >= 17 && hour <= 20)) {
      baseFare *= 1.2; // 20% surge
    }

    // Add platform fee and taxes
    const platformFee = 50;
    const taxes = baseFare * 0.05; // 5% GST
    const total = baseFare + platformFee + taxes;

    setFareEstimate({
      baseFare: Math.round(baseFare),
      platformFee,
      taxes: Math.round(taxes),
      total: Math.round(total),
      distance: distance
    });
  };

  const generateWhatsAppLink = (formData) => {
    const vehicle = vehicles[formData.vehicle];
    const message = `Hi Agami Cabs! 🚗

I'd like to book a ride:

📍 *Pickup:* ${formData.pickupLocation}
📍 *Drop:* ${formData.dropLocation || 'To be confirmed'}
📅 *Date:* ${formData.bookingDate.toLocaleDateString()}
🕐 *Time:* ${formData.bookingTime.toLocaleTimeString()}
🚗 *Vehicle:* ${vehicle.name}
👥 *Passengers:* ${formData.passengers}
🎯 *Service:* ${formData.serviceType}

${fareEstimate ? `💰 *Estimated Fare:* ₹${fareEstimate.total}` : ''}

Please confirm availability and final pricing.

Thank you!`;

    const encodedMessage = encodeURIComponent(message);
    return `https://wa.me/919999999999?text=${encodedMessage}`;
  };

  const onSubmit = (data) => {
    calculateFare();
    setBookingStep(3);
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-3xl p-8 w-full max-w-2xl max-h-[90vh] overflow-y-auto border border-white/10"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-3xl font-bold text-white mb-2">Book Your Ride</h2>
              <p className="text-emerald-400">You first, always</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-white/60 hover:text-white transition-colors duration-300"
            >
              <X size={24} />
            </button>
          </div>

          {/* Step 1: Booking Details */}
          {bookingStep === 1 && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="space-y-6"
            >
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                {/* Service Type */}
                <div>
                  <label className="block text-white font-semibold mb-3">Service Type</label>
                  <Controller
                    name="serviceType"
                    control={control}
                    render={({ field }) => (
                      <div className="grid grid-cols-2 gap-3">
                        {[
                          { value: 'airport', label: 'Airport Transfer', desc: 'To/From IGI Airport' },
                          { value: 'hourly', label: 'Hourly Rental', desc: 'Flexible hourly booking' },
                          { value: 'daily', label: 'Daily Rental', desc: 'Full day service' },
                          { value: 'intercity', label: 'Inter-city', desc: 'Delhi to nearby cities' }
                        ].map((service) => (
                          <motion.div
                            key={service.value}
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            className={`p-4 rounded-xl border-2 cursor-pointer transition-all duration-300 ${
                              field.value === service.value
                                ? 'border-emerald-500 bg-emerald-500/10'
                                : 'border-white/20 bg-slate-700/30 hover:border-emerald-400/50'
                            }`}
                            onClick={() => field.onChange(service.value)}
                          >
                            <h4 className="text-white font-semibold text-sm">{service.label}</h4>
                            <p className="text-white/60 text-xs mt-1">{service.desc}</p>
                          </motion.div>
                        ))}
                      </div>
                    )}
                  />
                </div>

                {/* Location Inputs */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-white font-semibold mb-2">
                      <MapPin className="w-4 h-4 inline mr-2" />
                      Pickup Location
                    </label>
                    <Controller
                      name="pickupLocation"
                      control={control}
                      rules={{ required: 'Pickup location is required' }}
                      render={({ field }) => (
                        <input
                          {...field}
                          type="text"
                          placeholder="Enter pickup address"
                          className="w-full bg-slate-700/50 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-white/50 focus:outline-none focus:border-emerald-500 transition-colors duration-300"
                        />
                      )}
                    />
                    {errors.pickupLocation && (
                      <p className="text-red-400 text-sm mt-1">{errors.pickupLocation.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-white font-semibold mb-2">
                      <MapPin className="w-4 h-4 inline mr-2" />
                      Drop Location
                    </label>
                    <Controller
                      name="dropLocation"
                      control={control}
                      render={({ field }) => (
                        <input
                          {...field}
                          type="text"
                          placeholder="Enter destination"
                          className="w-full bg-slate-700/50 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-white/50 focus:outline-none focus:border-emerald-500 transition-colors duration-300"
                        />
                      )}
                    />
                  </div>
                </div>

                {/* Date and Time */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-white font-semibold mb-2">
                      <Calendar className="w-4 h-4 inline mr-2" />
                      Date
                    </label>
                    <Controller
                      name="bookingDate"
                      control={control}
                      render={({ field }) => (
                        <DatePicker
                          selected={field.value}
                          onChange={field.onChange}
                          minDate={new Date()}
                          dateFormat="dd/MM/yyyy"
                          className="w-full bg-slate-700/50 border border-white/20 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-emerald-500 transition-colors duration-300"
                        />
                      )}
                    />
                  </div>

                  <div>
                    <label className="block text-white font-semibold mb-2">
                      <Clock className="w-4 h-4 inline mr-2" />
                      Time
                    </label>
                    <Controller
                      name="bookingTime"
                      control={control}
                      render={({ field }) => (
                        <DatePicker
                          selected={field.value}
                          onChange={field.onChange}
                          showTimeSelect
                          showTimeSelectOnly
                          timeIntervals={15}
                          timeCaption="Time"
                          dateFormat="h:mm aa"
                          className="w-full bg-slate-700/50 border border-white/20 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-emerald-500 transition-colors duration-300"
                        />
                      )}
                    />
                  </div>
                </div>

                {/* Vehicle Selection */}
                <div>
                  <label className="block text-white font-semibold mb-3">
                    <Car className="w-4 h-4 inline mr-2" />
                    Select Vehicle
                  </label>
                  <Controller
                    name="vehicle"
                    control={control}
                    render={({ field }) => (
                      <div className="grid md:grid-cols-2 gap-4">
                        {Object.entries(vehicles).map(([key, vehicle]) => (
                          <motion.div
                            key={key}
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            className={`p-4 rounded-xl border-2 cursor-pointer transition-all duration-300 ${
                              field.value === key
                                ? 'border-emerald-500 bg-emerald-500/10'
                                : 'border-white/20 bg-slate-700/30 hover:border-emerald-400/50'
                            }`}
                            onClick={() => field.onChange(key)}
                          >
                            <img 
                              src={vehicle.image} 
                              alt={vehicle.name}
                              className="w-full h-32 object-cover rounded-lg mb-3"
                            />
                            <h4 className="text-white font-semibold">{vehicle.name}</h4>
                            <p className="text-emerald-400 text-sm">₹{vehicle.baseRate}/km • {vehicle.capacity} seater</p>
                          </motion.div>
                        ))}
                      </div>
                    )}
                  />
                </div>

                {/* Passengers */}
                <div>
                  <label className="block text-white font-semibold mb-2">Number of Passengers</label>
                  <Controller
                    name="passengers"
                    control={control}
                    render={({ field }) => (
                      <select
                        {...field}
                        className="w-full bg-slate-700/50 border border-white/20 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-emerald-500 transition-colors duration-300"
                      >
                        {[1, 2, 3, 4, 5, 6, 7].map(num => (
                          <option key={num} value={num}>{num} Passenger{num > 1 ? 's' : ''}</option>
                        ))}
                      </select>
                    )}
                  />
                </div>

                {/* Action Buttons */}
                <div className="flex gap-4 pt-4">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    type="button"
                    onClick={calculateFare}
                    className="flex-1 bg-gradient-to-r from-blue-500 to-blue-600 px-6 py-3 rounded-xl text-white font-semibold hover:from-blue-600 hover:to-blue-700 transition-all duration-300 flex items-center justify-center space-x-2"
                  >
                    <Calculator className="w-5 h-5" />
                    <span>Get Fare Estimate</span>
                  </motion.button>
                  
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    type="submit"
                    className="flex-1 bg-gradient-to-r from-emerald-500 to-emerald-600 px-6 py-3 rounded-xl text-white font-semibold hover:from-emerald-600 hover:to-emerald-700 transition-all duration-300"
                  >
                    Continue to Book
                  </motion.button>
                </div>
              </form>
            </motion.div>
          )}

          {/* Step 2: Fare Estimate Display */}
          {fareEstimate && bookingStep !== 3 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-gradient-to-br from-emerald-500/10 to-emerald-600/5 border border-emerald-500/20 rounded-2xl p-6 mt-6"
            >
              <h3 className="text-xl font-bold text-white mb-4 flex items-center">
                <IndianRupee className="w-5 h-5 mr-2" />
                Fare Estimate
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between text-white/80">
                  <span>Base Fare ({fareEstimate.distance}km)</span>
                  <span>₹{fareEstimate.baseFare}</span>
                </div>
                <div className="flex justify-between text-white/80">
                  <span>Platform Fee</span>
                  <span>₹{fareEstimate.platformFee}</span>
                </div>
                <div className="flex justify-between text-white/80">
                  <span>Taxes (GST)</span>
                  <span>₹{fareEstimate.taxes}</span>
                </div>
                <div className="border-t border-white/20 pt-3">
                  <div className="flex justify-between text-white font-bold text-lg">
                    <span>Total Estimate</span>
                    <span className="text-emerald-400">₹{fareEstimate.total}</span>
                  </div>
                </div>
              </div>
              <p className="text-white/60 text-sm mt-4">
                *Final fare may vary based on actual distance and traffic conditions
              </p>
            </motion.div>
          )}

          {/* Step 3: Booking Confirmation */}
          {bookingStep === 3 && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="text-center space-y-6"
            >
              <div className="w-20 h-20 bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-full flex items-center justify-center mx-auto">
                <MessageCircle className="w-10 h-10 text-white" />
              </div>
              
              <div>
                <h3 className="text-2xl font-bold text-white mb-2">Booking Summary</h3>
                <p className="text-white/80">Complete your booking via WhatsApp or phone</p>
              </div>

              {fareEstimate && (
                <div className="bg-slate-700/30 rounded-2xl p-6">
                  <h4 className="text-white font-semibold mb-3">Trip Details</h4>
                  <div className="text-left space-y-2 text-white/80">
                    <p><strong>Vehicle:</strong> {vehicles[watchedValues.vehicle].name}</p>
                    <p><strong>Date:</strong> {watchedValues.bookingDate.toLocaleDateString()}</p>
                    <p><strong>Time:</strong> {watchedValues.bookingTime.toLocaleTimeString()}</p>
                    <p><strong>Estimated Fare:</strong> <span className="text-emerald-400 font-bold">₹{fareEstimate.total}</span></p>
                  </div>
                </div>
              )}

              <div className="flex gap-4">
                <motion.a
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  href={generateWhatsAppLink(watchedValues)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 bg-gradient-to-r from-green-500 to-green-600 px-6 py-3 rounded-xl text-white font-semibold hover:from-green-600 hover:to-green-700 transition-all duration-300 flex items-center justify-center space-x-2"
                >
                  <MessageCircle className="w-5 h-5" />
                  <span>Book via WhatsApp</span>
                </motion.a>
                
                <motion.a
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  href="tel:+919999999999"
                  className="flex-1 bg-gradient-to-r from-blue-500 to-blue-600 px-6 py-3 rounded-xl text-white font-semibold hover:from-blue-600 hover:to-blue-700 transition-all duration-300 flex items-center justify-center space-x-2"
                >
                  <Phone className="w-5 h-5" />
                  <span>Call Now</span>
                </motion.a>
              </div>

              <button
                onClick={() => setBookingStep(1)}
                className="text-emerald-400 hover:text-emerald-300 transition-colors duration-300"
              >
                ← Back to Edit Booking
              </button>
            </motion.div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default BookingModal;